import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import type { CasinoProfile, TableProfile, WheelStyle } from '~/domain/types';
import { WHEEL_STYLES, getWheel } from '~/domain/wheels';
import { deviatesFromStandard, formatCents, sortTables } from '~/domain/profiles';
import CasinoEditor from '~/components/CasinoEditor';
import TableEditor from '~/components/TableEditor';

/**
 * Casino configurator (SPEC.md §5.1): one casino, its house-wide settings, and
 * its tables.
 *
 * Picking where to play happens in `TableSelector`, which is what made this
 * simple. It used to hold a selector of its own, which meant two different
 * notions of "current" at once — the casino whose name you were editing and the
 * table the whole app was computing against, free to belong to different
 * venues. Here there is one casino and one table being edited, both within it.
 *
 * Making a table active is still offered, because after describing a table you
 * usually want to use it — but it is one explicit button, not a side effect of
 * clicking a row.
 */

export interface CasinoConfiguratorProps {
  casino: CasinoProfile;
  tables: readonly TableProfile[];
  /** The table the rest of the app is computing against, so it can be marked. */
  activeTableId: string;
  onChangeCasino: (casino: CasinoProfile) => void;
  onDeleteCasino: () => void;
  onChangeTable: (table: TableProfile) => void;
  /** Returns the table it made, so the configurator can move onto it. */
  onAddTable: (style: WheelStyle) => TableProfile;
  onDuplicateTable: (table: TableProfile) => TableProfile;
  onDeleteTable: (table: TableProfile) => void;
  onUseTable: (table: TableProfile) => void;
  onClose: () => void;
}

const CasinoConfigurator: Component<CasinoConfiguratorProps> = (props) => {
  const [editingId, setEditingId] = createSignal<string | null>(null);

  /**
   * Falls back to the active table when it belongs here, so opening the
   * configurator lands on the table you are actually playing.
   */
  const editing = createMemo<TableProfile | null>(() => {
    const tables = sortTables(props.tables);
    return (
      tables.find((table) => table.id === editingId()) ??
      tables.find((table) => table.id === props.activeTableId) ??
      tables[0] ??
      null
    );
  });

  const addTable = (style: WheelStyle) => setEditingId(props.onAddTable(style).id);

  const duplicate = (table: TableProfile) => setEditingId(props.onDuplicateTable(table).id);

  const remove = (table: TableProfile) => {
    if (!window.confirm(`Delete the table "${table.name}"?`)) return;
    props.onDeleteTable(table);
    setEditingId(null);
  };

  return (
    <div class="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-[13px] font-medium text-ink">{props.casino.name}</h2>
          <p class="mt-0.5 text-[11px] text-ink-faint">
            Odds, limits and chips are per table. Everything on screen follows whichever
            table is in use.
          </p>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          class="shrink-0 rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim hover:text-ink"
        >
          Done
        </button>
      </header>

      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Casino
        </h3>
        <CasinoEditor
          casino={props.casino}
          onChange={props.onChangeCasino}
          onDelete={() => {
            if (!window.confirm('Delete this casino and all its tables? Spreads are kept.')) return;
            props.onDeleteCasino();
          }}
        />
      </section>

      <section class="rounded-lg border border-line bg-surface-1 p-3">
        <h3 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Tables · {props.tables.length}
        </h3>

        <div class="space-y-0.5">
          <For each={sortTables(props.tables)}>
            {(table) => (
              <button
                type="button"
                onClick={() => setEditingId(table.id)}
                class="flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-[11px] transition-colors"
                classList={{
                  'border-placed bg-placed/15 text-ink': table.id === editing()?.id,
                  'border-line bg-surface-2 text-ink-dim hover:border-line-strong':
                    table.id !== editing()?.id,
                }}
              >
                <span class="truncate">{table.name}</span>
                <span class="flex shrink-0 items-center gap-1.5 text-[10px]">
                  <Show when={table.id === props.activeTableId}>
                    <span class="rounded bg-placed/30 px-1 text-ink">in use</span>
                  </Show>
                  <Show when={deviatesFromStandard(table)}>
                    <span class="rounded bg-remove/20 px-1 text-remove">non-standard</span>
                  </Show>
                  <span class="tnum text-ink-faint">
                    {table.wheelStyle === 'single-zero' ? '0' : '0/00'} ·{' '}
                    {formatCents(table.baseUnit, props.casino.currency)}
                  </span>
                </span>
              </button>
            )}
          </For>
        </div>

        <div class="mt-2 flex flex-wrap gap-2">
          <For each={WHEEL_STYLES}>
            {(style) => (
              <button
                type="button"
                onClick={() => addTable(style)}
                class="text-[10px] text-ink-faint hover:text-ink"
              >
                + {getWheel(style).label} table
              </button>
            )}
          </For>
        </div>
      </section>

      <Show
        when={editing()}
        fallback={
          <p class="rounded-lg border border-line bg-surface-1 p-3 text-[11px] text-ink-faint">
            This casino has no tables yet. Add one above — a casino with no table cannot be
            selected.
          </p>
        }
      >
        {(table) => (
          <section class="rounded-lg border border-line bg-surface-1 p-3">
            <div class="mb-2 flex items-center justify-between gap-2">
              <h3 class="truncate text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
                Table — {table().name}
              </h3>
              <div class="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => props.onUseTable(table())}
                  disabled={table().id === props.activeTableId}
                  class="rounded border border-line bg-surface-2 px-2 py-0.5 text-[10px] text-ink-dim enabled:hover:border-line-strong enabled:hover:text-ink disabled:opacity-40"
                >
                  {table().id === props.activeTableId ? 'In use' : 'Use this table'}
                </button>
                <button
                  type="button"
                  onClick={() => duplicate(table())}
                  class="rounded border border-line bg-surface-2 px-2 py-0.5 text-[10px] text-ink-dim hover:text-ink"
                  title="Most setups differ from another by a single limit"
                >
                  Duplicate
                </button>
              </div>
            </div>
            <TableEditor
              table={table()}
              currency={props.casino.currency}
              onChange={props.onChangeTable}
              onDelete={() => remove(table())}
            />
          </section>
        )}
      </Show>
    </div>
  );
};

export default CasinoConfigurator;
